import { computed, onMounted, onUnmounted, ref } from 'vue'
import { fetchPublishedPosts } from '../services/posts.service'
import type { LandingNewsPost } from '../types/landing.types'

/** Icono por defecto cuando la publicación no trae imagen. */
const DEFAULT_ICON = 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5'

/** Gradiente de respaldo cuando la publicación no trae imagen. */
const DEFAULT_IMAGE_CLASS = 'wl-ni-1'

const MAX_EXCERPT = 180

const dateFormatter = new Intl.DateTimeFormat('es-MX', {
  day: 'numeric',
  month: 'short',
  year: 'numeric'
})

/** Quita el HTML que el editor rich-text guarda en la descripción. */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

function formatDate(value: string): string {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : dateFormatter.format(date)
}

/**
 * Normaliza la ruta de imagen que guarda el backend
 * (`/uploads/publicaciones/<guid>.jpg`) a una ruta servible por la SPA.
 */
function resolveImageUrl(imageUrl: string | null | undefined): string | null {
  if (!imageUrl) return null
  const value = imageUrl.trim()
  if (!value) return null
  if (/^https?:\/\//i.test(value)) return value
  return value.startsWith('/') ? value : `/${value}`
}

function toExcerpt(description: string): string {
  const text = stripHtml(description)
  if (text.length <= MAX_EXCERPT) return text
  return `${text.slice(0, MAX_EXCERPT).trimEnd()}…`
}

/**
 * Estado de la sección de blog de la landing.
 *
 * Muestra **solo** las publicaciones reales del backend
 * (`GET /api/posts/published`). Si la API no devuelve filas o falla, la
 * sección queda en estado vacío: no se inventan artículos que no existen en
 * la base de datos.
 */
export function useLandingNews() {
  const posts = ref<LandingNewsPost[]>([])
  const loading = ref(true)
  const error = ref<string | null>(null)

  let controller: AbortController | null = null

  async function load() {
    loading.value = true
    error.value = null
    controller = new AbortController()

    try {
      const published = await fetchPublishedPosts(6, controller.signal)

      posts.value = published.map((post, index) => ({
        id: post.id,
        category: 'Blog',
        categoryClass: 'wl-nc-blog',
        date: formatDate(post.createdAt),
        title: post.title,
        excerpt: toExcerpt(post.description),
        imageClass: DEFAULT_IMAGE_CLASS,
        icon: DEFAULT_ICON,
        featured: index === 0,
        imageUrl: resolveImageUrl(post.imageUrl),
        link: `/noticias/${post.id}`
      }))
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return
      posts.value = []
      error.value = (err as Error)?.message ?? 'No fue posible cargar las publicaciones.'
    } finally {
      loading.value = false
    }
  }

  onMounted(load)
  onUnmounted(() => controller?.abort())

  const featured = computed(() => posts.value.filter((post) => post.featured))
  const side = computed(() => posts.value.filter((post) => !post.featured))
  const isEmpty = computed(() => !loading.value && posts.value.length === 0)

  return { posts, featured, side, loading, isEmpty, error, reload: load }
}
